import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { BookOpen, Search, Filter, Loader2, DollarSign, Building2, Calendar, ArrowUpCircle, ArrowDownCircle, FileDown } from 'lucide-react';
import financeService from '../services/finance';
import cabangService from '../services/cabang';
import usePdfExport from '../hooks/usePdfExport';

const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);

const JurnalUmum = () => {
  const [jurnalEntries, setJurnalEntries] = useState([]);
  const [akunList, setAkunList] = useState([]);
  const [cabangList, setCabangList] = useState([]);
  const [accountSummary, setAccountSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterAkun, setFilterAkun] = useState('All');
  const [filterCabang, setFilterCabang] = useState('All');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedAkun, setSelectedAkun] = useState(null);

  const { exporting, exportPdf, contentRef } = usePdfExport({
    filename: 'jurnal-umum',
    scale: 3,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };
      if (filterAkun !== 'All') params.akunId = filterAkun;
      if (filterCabang !== 'All') params.cabangId = filterCabang;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const data = await financeService.getJurnal(params);
      setJurnalEntries(data.jurnal || []);
      setAccountSummary(data.accountSummary || []);
      if (data.pagination) setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, filterAkun, filterCabang, dateRange]);

  const fetchAkun = useCallback(async () => {
    try {
      const data = await financeService.getAkun({ isActive: 'true' });
      setAkunList(data.akun || []);
    } catch (err) { }
  }, []);

  const fetchCabang = useCallback(async () => {
    try {
      const data = await cabangService.getCabang();
      setCabangList(data.cabang || []);
    } catch (err) { }
  }, []);

  useEffect(() => { fetchData(); fetchAkun(); fetchCabang(); }, [fetchData, fetchAkun, fetchCabang]);

  const calculateTotals = () => {
    let totalIn = 0, totalOut = 0;
    jurnalEntries.forEach(e => {
      if (e.tipeTransaksi === 'PEMASUKAN') totalIn += e.jumlah;
      else totalOut += e.jumlah;
    });
    return { totalIn, totalOut, balance: totalIn - totalOut };
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-4 animate-fade-in" ref={contentRef}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Jurnal Umum</h2>
          <p className="mt-1 text-sm text-gray-500">Complete journal with account history and running balance</p>
        </div>
        <button onClick={() => exportPdf('Jurnal-Umum')} disabled={exporting} className="btn-primary text-sm">
          {exporting ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Exporting...</span></> : <><FileDown className="w-4 h-4" /><span>Export PDF</span></>}
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="px-4 py-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Akun</label>
              <select value={filterAkun} onChange={e => { setFilterAkun(e.target.value); setPage(1); }} className="select-field text-sm">
                <option value="All">All Akun</option>
                {akunList.map(a => <option key={a._id} value={a._id}>{a.kodeAkun} - {a.namaAkun}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cabang</label>
              <select value={filterCabang} onChange={e => { setFilterCabang(e.target.value); setPage(1); }} className="select-field text-sm">
                <option value="All">All Cabang</option>
                {cabangList.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
              <input type="date" value={dateRange.start} onChange={e => { setDateRange(p => ({ ...p, start: e.target.value })); setPage(1); }} className="input-field text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
              <input type="date" value={dateRange.end} onChange={e => { setDateRange(p => ({ ...p, end: e.target.value })); setPage(1); }} className="input-field text-sm" />
            </div>
          </div>
        </div>
      </div>

      {/* Account Summary */}
      {accountSummary.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Ringkasan Per Akun</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Kode</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Nama Akun</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Pemasukan</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Pengeluaran</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Saldo</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-50">
                {accountSummary.map(acc => (
                  <tr key={acc._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedAkun(selectedAkun?._id === acc._id ? null : acc)}>
                    <td className="px-4 py-2 text-xs font-mono text-gray-500">{acc.kodeAkun}</td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{acc.namaAkun}</td>
                    <td className="px-4 py-2 text-right text-sm font-semibold text-green-600">{formatRupiah(acc.totalPemasukan)}</td>
                    <td className="px-4 py-2 text-right text-sm font-semibold text-red-600">{formatRupiah(acc.totalPengeluaran)}</td>
                    <td className="px-4 py-2 text-right text-sm font-bold text-blue-600">{formatRupiah(acc.totalPemasukan - acc.totalPengeluaran)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Journal Entries */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            {filterAkun !== 'All' ? `Riwayat: ${akunList.find(a => a._id === filterAkun)?.namaAkun || ''}` : 'Semua Transaksi'}
          </h3>
          <span className="text-xs text-gray-500">{pagination?.totalItems || 0} entries</span>
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-20"><Loader2 className="h-10 w-10 animate-spin text-blue-600" /><span className="mt-4 text-gray-500">Loading...</span></div>
        ) : jurnalEntries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Tanggal</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">No. Transaksi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Akun</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Deskripsi</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Debit (Pengeluaran)</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Kredit (Pemasukan)</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Saldo</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-50">
                {jurnalEntries.map((entry, idx) => (
                  <tr key={entry._id || idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                      {format(new Date(entry.tanggal), 'dd/MM/yyyy')}
                    </td>
                    <td className="px-4 py-2 text-xs font-mono text-gray-500 whitespace-nowrap">{entry.nomorTransaksi}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">{entry.akun?.kodeAkun} - {entry.akun?.namaAkun}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 max-w-[250px] truncate">{entry.deskripsi}</td>
                    <td className="px-4 py-2 text-right text-sm font-semibold text-red-600 whitespace-nowrap">
                      {entry.tipeTransaksi === 'PENGELUARAN' ? formatRupiah(entry.jumlah) : '-'}
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-semibold text-green-600 whitespace-nowrap">
                      {entry.tipeTransaksi === 'PEMASUKAN' ? formatRupiah(entry.jumlah) : '-'}
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-bold text-blue-600 whitespace-nowrap">
                      {formatRupiah(entry.saldoBerjalan || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td colSpan="4" className="px-4 py-2 text-right text-sm text-gray-700">Total</td>
                  <td className="px-4 py-2 text-right text-sm text-red-600">{formatRupiah(totals.totalOut)}</td>
                  <td className="px-4 py-2 text-right text-sm text-green-600">{formatRupiah(totals.totalIn)}</td>
                  <td className="px-4 py-2 text-right text-sm text-blue-600">{formatRupiah(totals.balance)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No journal entries found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-between items-center card px-4 py-3">
          <span className="text-sm text-gray-500">Page {pagination.currentPage} of {pagination.totalPages}</span>
          <div className="flex space-x-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-secondary text-xs">Previous</button>
            <button disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)} className="btn-secondary text-xs">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JurnalUmum;
